import { orderBy, uniqueId } from 'lodash-es';
import {
  ICriteria,
  ICriteriaFormModal,
  ICriteriaPayload,
  ICriteriaVisibilityRule,
} from '~/connect/types';

export const DEFAULT_VALUE_LABEL = 'N/A';

export function sorterByName(values: ICriteria['values']) {
  const labelSorter = (value: ICriteria['values'][number]) =>
    value.label.toLowerCase();

  return orderBy(values, labelSorter);
}

/**
 * Form-only key of a value: new values have no id until the API creates them
 */
export const createValueUid = () => uniqueId('criteria-value-');

export function criteriaToForm(criteria?: ICriteria): ICriteriaFormModal {
  if (!criteria) {
    return {
      name: '',
      visible: false,
      values: [
        { uid: createValueUid(), label: DEFAULT_VALUE_LABEL, default: true },
      ],
      visibilityRules: [],
    };
  }

  return {
    name: criteria.name,
    visible: criteria.visible,
    values: criteria.values.map(({ id, label, default: isDefault }) => ({
      uid: id,
      id,
      label,
      default: isDefault,
    })),
    visibilityRules: (criteria.visibilityRules ?? []).map((rule) => ({
      valueUids: rule.valueIds,
      automakerGroupIds: rule.automakerGroupIds,
    })),
  };
}

/**
 * Rules reference their values by id when it exists, and by label for the new ones: the API resolves them
 */
export function formToPayload(form: ICriteriaFormModal): ICriteriaPayload {
  const valueByUid = new Map(form.values.map((value) => [value.uid, value]));
  const toPayloadValue = ({
    id,
    label,
  }: ICriteriaFormModal['values'][number]) => (id ? { id, label } : { label });

  return {
    name: form.name,
    visible: form.visible,
    values: form.values.map(toPayloadValue),
    visibilityRules: form.visibilityRules
      // empty rows are ignored, partially filled ones are rejected by the form validation
      .filter(
        (rule) =>
          rule.valueUids.length > 0 && rule.automakerGroupIds.length > 0,
      )
      .map((rule) => ({
        values: rule.valueUids.flatMap((uid) => {
          const value = valueByUid.get(uid);
          return value ? [toPayloadValue(value)] : [];
        }),
        automakerGroupIds: rule.automakerGroupIds,
      })),
  };
}

export function removeValueFromRules(
  rules: Array<ICriteriaVisibilityRule> | undefined,
  removedValueId: string,
): Array<ICriteriaVisibilityRule> {
  return (rules ?? [])
    .map((rule) => ({
      ...rule,
      valueIds: rule.valueIds.filter((valueId) => valueId !== removedValueId),
    }))
    .filter((rule) => rule.valueIds.length > 0);
}
